import { useCallback, useRef, useState } from 'react';

import { css, useTheme } from '@emotion/react';
import {
  DropdownMenu2,
  dropdownMenu2Classes,
  Grid,
  InfoButton,
  MenuOptionItem,
  Select,
  Tooltip,
  Typography,
} from '@kc-console/common-ui';
import {
  useFetchUserDetailInfo,
  usePatchUserDetailInfo,
  useServiceAppState,
} from '@kc-console/context';
import { useTranslation } from '@kc-console/core';
import IcSettingType1Regular from '@kep/kcds-icons/IcSettingType1Regular';

import { getLanguageLabel, LanguageTypeEnum } from '../../../../constants/language';
import { getMacHotKeyModalData, getWindowsHotKeyModalData } from '../../../../utils/hotkey';
import GnbIconButton from '../GnbIconButton';
import { ContextMenuToggleItem } from './ContextMenuToggleItem';
import HotkeyModal from './HotkeyModal';
import { PanelDetailToggleItem, PanelListToggleItem } from './PanelToggleItem';

import type { DropdownMenu2ItemData } from '@kc-console/common-ui';

const SettingMenu = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const anchor = useRef<HTMLDivElement | null>(null);
  const [open, setOpen] = useState(false);
  const { refetch } = useFetchUserDetailInfo();

  const { mutate } = usePatchUserDetailInfo();

  const [modalOpen, setModalOpen] = useState(false);

  const {
    state: { userId, displayLanguage },
  } = useServiceAppState();

  const macHotKeyModalData = getMacHotKeyModalData();
  const windowsHotKeyModalData = getWindowsHotKeyModalData();

  /**
   * patchUserDetailInfo mutate 사용 시 주의사항: 사용자의 상세 정보를 모두 전달해야 함.
   * 관련 이슈: https://jira.daumkakao.com/browse/KIC-48465
   */
  const handleChangeLanguage = useCallback(
    async (val: string) => {
      const { data: latestData } = await refetch();

      mutate({ userId, user: { ...latestData, language_code: val } });
      setOpen(false);
    },
    [mutate, refetch, userId],
  );

  // NOTE: 현재 modal open 코드가 없음. 추후 추가 필요
  const handleCloseModal = () => setModalOpen(false);

  const items: DropdownMenu2ItemData[] = [
    {
      type: 'option',
      text: null,
      interactive: false,
      renderItem: params => (
        <MenuOptionItem
          {...params}
          key={params.key}
          renderContent={() => (
            <Typography
              variant="label/semibold/xs"
              css={{ color: theme.color['text/weakest/default'] }}
            >
              {t('layout:setting.language.title')}
            </Typography>
          )}
        />
      ),
    },
    {
      type: 'option',
      text: null,
      interactive: false,
      renderItem: params => (
        <MenuOptionItem
          {...params}
          key={params.key}
          renderContent={() => (
            <Select
              block
              size="lg"
              autoWidth="as-input"
              value={displayLanguage}
              disablePortal
              onChange={handleChangeLanguage}
              placeholderOnEmpty={false}
              renderValue={value => <>{getLanguageLabel(value as LanguageTypeEnum)}</>}
              options={Object.values(LanguageTypeEnum).map(value => {
                return {
                  value,
                  text: getLanguageLabel(value),
                };
              })}
            />
          )}
        />
      ),
    },
    {
      type: 'option',
      text: null,
      interactive: false,
      showDividerAbove: true,
      renderItem: params => (
        <MenuOptionItem
          {...params}
          key={params.key}
          renderContent={() => (
            <Grid container alignItems="center" gap={0.5}>
              <Typography
                variant="label/semibold/xs"
                css={{ color: theme.color['text/weakest/default'] }}
              >
                {t('layout:setting.convenience_features')}
              </Typography>
              <InfoButton
                size="sm"
                tooltipProps={{
                  message: t('layout:setting.convenience_features.description'),
                }}
              />
            </Grid>
          )}
        />
      ),
    },
    {
      type: 'option',
      text: null,
      interactive: false,
      renderItem: params => {
        return (
          <MenuOptionItem
            {...params}
            key={params.key}
            renderContent={() => <ContextMenuToggleItem />}
          />
        );
      },
    },
    {
      type: 'option',
      text: null,
      interactive: false,
      showDividerAbove: true,
      renderItem: params => (
        <MenuOptionItem
          {...params}
          key={params.key}
          renderContent={() => (
            <Grid container alignItems="center" gap={0.5}>
              <Typography
                variant="label/semibold/xs"
                css={{ color: theme.color['text/weakest/default'] }}
              >
                {t('layout:setting.panel.title')}
              </Typography>
              <InfoButton
                size="sm"
                tooltipProps={{
                  message: t('layout:setting.panel.description'),
                }}
              />
            </Grid>
          )}
        />
      ),
    },
    {
      type: 'option',
      text: null,
      interactive: false,
      renderItem: params => {
        return (
          <MenuOptionItem
            {...params}
            key={params.key}
            renderContent={() => <PanelListToggleItem />}
          />
        );
      },
    },
    {
      type: 'option',
      text: null,
      interactive: false,
      renderItem: params => {
        return (
          <MenuOptionItem
            {...params}
            key={params.key}
            renderContent={() => <PanelDetailToggleItem />}
          />
        );
      },
    },
  ];

  const userOs = navigator.userAgent.toLowerCase();
  const isWindows = /windows/.test(userOs);

  return (
    <>
      <Tooltip message={t('layout:setting.title')}>
        <div ref={anchor}>
          <GnbIconButton
            disableRipple
            aria-expanded={open}
            aria-haspopup="menu"
            aria-label={t('layout:setting.title')}
            onClick={() => setOpen(prev => !prev)}
          >
            <IcSettingType1Regular />
          </GnbIconButton>
        </div>
      </Tooltip>
      <DropdownMenu2
        anchorEl={anchor.current}
        open={open}
        onClose={() => setOpen(false)}
        items={items}
        css={css`
          .${dropdownMenu2Classes.paper} {
            width: 240px;
          }
        `}
      />
      <HotkeyModal
        open={modalOpen}
        onClose={handleCloseModal}
        data={isWindows ? windowsHotKeyModalData : macHotKeyModalData}
      />
    </>
  );
};

export default SettingMenu;
